import { useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import type { AgentCard, AgentHealth, DecisionDTO, ProjectBudget } from "@roomate/core";
import { useMe, useMission, useStopRun } from "../api.ts";
import { BUDGET_STATE_LABEL, BUDGET_TONE, budgetFor, budgetLine, canAct, costLine, filtersFromParams, HEALTH_LABEL, HOST_LABEL, MISSION_FILTER_KEYS, oneLine, sortCards, withFilter, type MissionFilterKey } from "../lib/mission.ts";
import { cx, ErrorNote } from "../ui.tsx";

/*
 * The mission card grid (docs/specs/mission-control.md §4.3): one card per agent, replacing "Working now" on `/`.
 * Filters live in the URL query so a filtered view can be shared. Live updates and the stall tick are MC5b.
 */

const HEALTH_TONE: Record<AgentHealth, string> = {
  off_track: "bg-high/10 text-high",
  needs_you: "bg-signal-soft text-[color:var(--medium)]",
  stalled: "bg-signal-soft text-[color:var(--medium)]",
  near_budget: "bg-signal-soft text-[color:var(--medium)]",
  on_track: "bg-paper-2 text-[color:var(--ok)]",
  idle: "bg-paper-2 text-ink-3",
};

const CRITERION_MARK: Record<AgentCard["criteria"][number]["status"], string> = { met: "✓", not_met: "✕", unknown: "?", pending: "·" };

const FILTER_LABEL: Record<MissionFilterKey, string> = { project: "Project", host: "Runtime", agent: "Agent", deliverable: "Work type", health: "Health", tier: "Risk" };

export function MissionGrid({ renderDecision }: { renderDecision: (d: DecisionDTO, canAnswer: boolean) => ReactNode }) {
  const [params, setParams] = useSearchParams();
  const filters = filtersFromParams(params);
  const mission = useMission(filters);
  const me = useMe().data!;
  const cards = sortCards(mission.data?.cards ?? []);
  const active = MISSION_FILTER_KEYS.some((k) => filters[k]);

  // A hub without GET /mission (or a transient failure) must not break the home screen: say so quietly, keep the rest.
  if (mission.isError && !mission.data)
    return (
      <p className="os-rise mt-14 text-[13px] text-ink-3" data-testid="mission-unavailable">
        Agent cards are unavailable right now.
      </p>
    );
  if (!mission.data) return null;
  if (cards.length === 0 && !active) return null;

  const f = mission.data.filters;
  const options: Record<MissionFilterKey, { value: string; label: string }[]> = {
    project: f.projects.map((p) => ({ value: p.id, label: p.name })),
    host: f.hosts.map((h) => ({ value: h, label: HOST_LABEL[h] ?? h })),
    agent: f.agents.map((a) => ({ value: a.id, label: a.name })),
    deliverable: f.deliverables.map((d) => ({ value: d, label: d })),
    health: (Object.keys(HEALTH_LABEL) as AgentHealth[]).map((h) => ({ value: h, label: HEALTH_LABEL[h] })),
    tier: ["low", "medium", "high", "critical"].map((t) => ({ value: t, label: t })),
  };

  return (
    <section className="os-rise mt-14" style={{ ["--i" as string]: 2 }} aria-label="Mission control" data-testid="mission-grid">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
        <span className="mr-2">Agents</span>
        {MISSION_FILTER_KEYS.map((k) => (
          <select
            key={k}
            className="rounded-lg border border-rule bg-paper px-2 py-1 text-[12.5px]"
            aria-label={FILTER_LABEL[k]}
            data-testid={`mission-filter-${k}`}
            value={filters[k] ?? ""}
            onChange={(e) => setParams(withFilter(params, k, e.target.value), { replace: true })}
          >
            <option value="">{FILTER_LABEL[k]}: all</option>
            {/* A shared link can carry a value the server's options no longer list; keep it selectable. */}
            {(filters[k] && !options[k].some((o) => o.value === filters[k]) ? [...options[k], { value: filters[k]!, label: filters[k]! }] : options[k]).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ))}
      </div>
      {cards.length === 0 ? (
        <p className="text-[13px] text-ink-3" data-testid="mission-empty">
          No agents match these filters.
        </p>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {cards.map((c) => (
            <MissionCard key={c.actorId} card={c} budget={budgetFor(mission.data.today.budgets, c.projectId)} canAct={canAct(me)} me={me} renderDecision={renderDecision} />
          ))}
        </ul>
      )}
    </section>
  );
}

function MissionCard({ card: c, budget, canAct: allowed, me, renderDecision }: { card: AgentCard; budget: ProjectBudget | null; canAct: boolean; me: { id: string; access?: string }; renderDecision: (d: DecisionDTO, canAnswer: boolean) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const stop = useStopRun();
  const initials = c.name.replace(/-agent$/, "").slice(0, 2).toUpperCase();
  const { met, total } = c.acceptance;
  const running = c.status === "starting" || c.status === "running";
  const last = c.lastOutput ? oneLine(c.lastOutput.text) : "";
  return (
    <li className="glass min-w-0 rounded-2xl p-4" data-testid="mission-card" data-health={c.health} data-agent={c.name}>
      <div className="flex items-center gap-3">
        <span className="glass grid h-9 w-9 shrink-0 place-items-center rounded-full text-[11px] font-semibold tracking-wide text-ink-2">{initials}</span>
        <span className="min-w-0 flex-1 truncate text-[14px] font-medium">{c.name}</span>
        {c.host && <span className="rounded-full bg-paper-2 px-2 py-0.5 text-[11px] text-ink-2">{HOST_LABEL[c.host] ?? c.host}</span>}
        {c.deliverable && <span className="rounded-full bg-paper-2 px-2 py-0.5 text-[11px] text-ink-2">{c.deliverable}</span>}
      </div>

      <div className="mt-3 min-w-0" data-testid="mission-goal">
        {c.goal ? (
          <>
            <p className="truncate text-[14px]">{c.goal.taskTitle}</p>
            {c.goal.intentTitle && <p className="truncate text-[12px] text-ink-3">{c.goal.intentTitle}</p>}
          </>
        ) : (
          <p className="text-[13px] text-ink-3">Nothing running</p>
        )}
      </div>

      {total > 0 && (
        <div className="mt-3">
          <button className="flex w-full items-center gap-2 text-left text-[12px] text-ink-2" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="mission-criteria-toggle">
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/[0.08]">
              <span className="block h-full bg-ink-2" style={{ width: `${(met / total) * 100}%` }} />
            </span>
            <span className="tabular-nums">
              {met}/{total}
            </span>
          </button>
          {open && (
            <ul className="mt-2 space-y-1 text-[12.5px]" data-testid="mission-criteria">
              {c.criteria.map((k) => (
                <li key={k.id} className={cx("flex gap-2", k.source === "reported" && "opacity-60")}>
                  <span className="w-3 shrink-0 text-center">{CRITERION_MARK[k.status]}</span>
                  <span>{k.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px]">
        <span className={cx("rounded-full px-2.5 py-0.5 font-medium", HEALTH_TONE[c.health])} data-testid="mission-health">
          {HEALTH_LABEL[c.health]}
        </span>
        {c.risk && (
          <span className="text-ink-2" data-testid="mission-risk" title={c.risk.reasons.join("; ")}>
            {c.risk.tier} risk
          </span>
        )}
        <span className="ml-auto tabular-nums text-ink-3" data-testid="mission-cost">
          {costLine(c.costUsd, c.costCapUsd)}
        </span>
      </div>
      {budget && (
        <p className="mt-1.5 flex flex-wrap items-center justify-end gap-2 text-[12px] tabular-nums" data-testid="mission-budget" data-budget-state={budget.state}>
          {BUDGET_STATE_LABEL[budget.state] && (
            <span className={BUDGET_TONE[budget.state]} data-testid="mission-budget-state">
              {BUDGET_STATE_LABEL[budget.state]}
            </span>
          )}
          <span className="text-ink-3">{budgetLine(budget)}</span>
        </p>
      )}

      {c.health === "needs_you" && c.needsYou.length === 0 && c.waitingOn.length > 0 && <p className="mt-2 text-[12px] text-ink-3">Waiting on {c.waitingOn.join(", ")}</p>}
      {last && (
        <p className="mt-2 truncate text-[12px] text-ink-3" title={last} data-testid="mission-last-output">
          {last}
        </p>
      )}

      {c.needsYou.map((d) => (
        <div key={d.id} className="mt-3 border-t border-rule pt-3" data-testid="mission-decision">
          {/* Same rule as the Run page: an admin, or the decision's assignee. */}
          {renderDecision(d, allowed && (me.access === "admin" || d.assigneeId === me.id))}
        </div>
      ))}

      <div className="mt-3 flex items-center gap-3 text-[12.5px]">
        {c.runId && (
          <Link to={`/runs/${c.runId}`} className="text-ink-2 underline decoration-rule hover:text-ink">
            Open run
          </Link>
        )}
        {allowed && running && c.runId && (
          <button className="ml-auto text-high hover:underline disabled:opacity-50" onClick={() => stop.mutate(c.runId!)} disabled={stop.isPending} data-testid="mission-stop">
            Stop
          </button>
        )}
      </div>
      <ErrorNote error={stop.error} />
    </li>
  );
}
